import { useState } from 'react'
import {
  Circle,
  Download,
  FileSpreadsheet,
  LayoutGrid,
  Loader2,
  RectangleHorizontal,
  Share2,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type ToolbarProps = {
  detailedView: boolean
  saving: boolean
  exporting: boolean
  tableCount: number
  tableLimit: number
  onAddRoundTable: () => void
  onAddRectangleTable: () => void
  onToggleDetailedView: () => void
  onChangeTableLimit: (value: number) => void
  onResetAll: () => void
  onDownloadPdf: () => void
  onDownloadCsv: () => void
  onSaveAndShare: () => void
}

export default function Toolbar({
  detailedView,
  saving,
  exporting,
  tableCount,
  tableLimit,
  onAddRoundTable,
  onAddRectangleTable,
  onToggleDetailedView,
  onChangeTableLimit,
  onResetAll,
  onDownloadPdf,
  onDownloadCsv,
  onSaveAndShare,
}: ToolbarProps) {
  const atLimit = tableLimit > 0 && tableCount >= tableLimit

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-black/5 bg-white p-3 shadow-sm">
      <Button type="button" size="sm" onClick={onAddRoundTable} disabled={atLimit}>
        <Circle className="mr-1.5 size-4" />
        Round table
      </Button>
      <Button type="button" size="sm" onClick={onAddRectangleTable} variant="secondary" disabled={atLimit}>
        <RectangleHorizontal className="mr-1.5 size-4" />
        Rectangle table
      </Button>

      <div className="ml-1 flex items-center gap-1.5">
        <Label htmlFor="table-limit" className="text-xs font-medium text-muted-foreground">
          Limit
        </Label>
        <Input
          id="table-limit"
          type="number"
          min={0}
          max={500}
          value={tableLimit || ''}
          placeholder="∞"
          onChange={(e) => {
            const n = parseInt(e.target.value, 10)
            onChangeTableLimit(Number.isFinite(n) && n >= 0 ? n : 0)
          }}
          className="h-8 w-16 text-sm"
        />
        <span className="text-xs text-muted-foreground">
          {tableCount}/{tableLimit > 0 ? tableLimit : '∞'}
        </span>
      </div>

      <div className="mx-1 h-6 w-px bg-black/10" />

      <Button type="button" size="sm" variant="outline" onClick={onToggleDetailedView}>
        <LayoutGrid className="mr-1.5 size-4" />
        {detailedView ? 'Compact view' : 'Detailed view'}
      </Button>

      <div className="ml-auto flex items-center gap-2">
        <ResetButton onConfirm={onResetAll} />
        <Button type="button" size="sm" variant="outline" onClick={onDownloadCsv}>
          <FileSpreadsheet className="mr-1.5 size-4" />
          Download CSV
        </Button>
        <Button type="button" size="sm" variant="outline" disabled={exporting} onClick={onDownloadPdf}>
          {exporting ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Download className="mr-1.5 size-4" />}
          Download PDF
        </Button>
        <Button type="button" size="sm" disabled={saving} onClick={onSaveAndShare}>
          {saving ? <Loader2 className="mr-1.5 size-4 animate-spin" /> : <Share2 className="mr-1.5 size-4" />}
          Save & share
        </Button>
      </div>
    </div>
  )
}

function ResetButton({ onConfirm }: { onConfirm: () => void }) {
  const [open, setOpen] = useState(false)
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" size="sm" variant="ghost">
          <Trash2 className="mr-1.5 size-4" />
          Reset
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Clear the planner?</DialogTitle>
          <DialogDescription>
            This removes all tables, guests, and assignments. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              onConfirm()
              setOpen(false)
            }}
          >
            Reset
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
